import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemButton } from "@mui/material";
import ReactMarkdown from "react-markdown";
import AssessmentViewer from './AssessmentViewer';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

//pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.js';

export default function Dashboard({ user, applicationId, applicationData, onBack, onAnalyze }) {
  const navigate = useNavigate();
  const [currentStatus, setCurrentStatus] = useState(applicationData?.status);
  const [currentCategory, setCurrentCategory] = useState(applicationData?.category);
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assessmentText, setAssessmentText] = useState("");
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [nonComplianceIssues, setNonComplianceIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/applications/${applicationId}/assessments/`, {
      credentials: "include"
    })
      .then(res => res.json())
      .then(setAssessments);
  }, [applicationId]);

  // Fetch attachments when application changes
  useEffect(() => {
    fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/applications/${applicationId}/attachments/`, {
      credentials: "include"
    })
      .then(res => res.json())
      .then(setAttachments);
  }, [applicationId]);

  // Add this near your other useEffect declarations
  useEffect(() => {
    const fetchApplicationDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/applications/${applicationId}`,
          { credentials: "include" }
        );
        if (response.ok) {
          const data = await response.json();
          setCurrentStatus(data.status);
          setNonComplianceIssues(data.non_compliance_issues || []);
        }
      } catch (error) {
        console.error('Error fetching application details:', error);
      }
    };

    fetchApplicationDetails();
  }, [applicationId]);

  // Add function to fetch latest application status
  const fetchApplicationStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/applications/${applicationId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setCurrentStatus(data.status);
      }
    } catch (error) {
      console.error('Error fetching application status:', error);
    }
  };

  // Update dialog handling to fetch status after close
  const handleAssessmentClose = () => {
    setAssessmentOpen(false);
    fetchApplicationStatus();
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const handlePrevPage = () => {
    setPageNumber(pageNumber - 1);
  };

  const handleNextPage = () => {
    setPageNumber(pageNumber + 1);
  };

  // Category update function
  const updateApplicationCategory = async (category) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000"}/applications/${applicationId}/category`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ category: category })
      });
      
      if (response.ok) {
        // Update the local state to reflect the change
        setCurrentCategory(category);
        // Also update the applicationData object if it exists
        if (applicationData) {
          applicationData.category = category;
        }
      } else {
        console.error("Failed to update category");
      }
    } catch (err) {
      console.error("Error updating category:", err);
    }
  };

  // Determine color for status
  const statusColor = applicationData.status === "new" ? "error.main" : "text.primary";

  // Add this function to handle file selection
  const handleFileSelect = async (file) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/applications/${applicationId}/attachments/${encodeURIComponent(file.filename)}`,
        { 
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error('Failed to fetch PDF');
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setSelectedFile({
        url: blobUrl,
        name: file.filename
      });
    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  };

  // Clean up blob URL when dialog closes
  useEffect(() => {
    return () => {
      if (pdfBlob) {
        URL.revokeObjectURL(pdfBlob);
      }
    };
  }, [pdfBlob]);

  const handleDownloadPDF = async () => {
    const assessmentId = selectedAssessment ? selectedAssessment.id : 'initial';
    const url = `${process.env.REACT_APP_API_URL}/applications/${applicationId}/assessments/${assessmentId}/pdf`;
    
    try {
      const response = await fetch(url, { credentials: 'include' });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `assessment-${applicationId}-${assessmentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleDownloadWordChecklist = async () => {
    const assessmentId = selectedAssessment ? selectedAssessment.id : 'initial';
    const url = `${process.env.REACT_APP_API_URL}/applications/${applicationId}/assessments/${assessmentId}/checklist/docx`;
    
    try {
      const response = await fetch(url, { credentials: 'include' });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `assessment-${applicationId}-${assessmentId}-checklist.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#f7fafc" }}>
      {/* Left menu */}
      <Box sx={{ width: 220, background: "#f5f5f5", p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Designer Portal</Typography>
        <Button fullWidth sx={{ mb: 1 }}>Dashboard</Button>
        <Button fullWidth sx={{ mb: 1 }}>My Projects</Button>
        <Button fullWidth sx={{ mb: 1 }}>Messages</Button>
        <Button fullWidth sx={{ mb: 1 }}>Settings</Button>
        <Button 
          fullWidth 
          sx={{ mb: 1 }}
          onClick={() => navigate(`/applications/${applicationId}/workitems`)}
        >
          Work Items
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          sx={{ mt: 2 }}
          onClick={onBack}
        >
          Back to Applications
        </Button>
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Attachments</Typography>
          <List>
            {attachments.length === 0 && (
              <ListItem>
                <Typography variant="body2" color="text.secondary">No attachments.</Typography>
              </ListItem>
            )}
            {attachments.map((att, idx) => (
              <ListItemButton
                key={idx}
                onClick={() => handleFileSelect(att)}
              >
                <Typography variant="body2">{att.filename}</Typography>
              </ListItemButton>
            ))}
          </List>
      </Box>
      {/* Main content */}
      <Box sx={{ flex: 1, p: 4 }}>
        <Paper sx={{ p: 4, maxWidth: 700, mx: "auto", mt: 4 }}>
          <Typography variant="h4" sx={{ mb: 2 }}>Dwelling</Typography>
          <TextField label="Site Address" value={applicationData.address || ""} fullWidth sx={{ mb: 2 }} InputProps={{ readOnly: true }}/>
          <TextField label="Description of Project" value={applicationData.proposed_development || ""} fullWidth sx={{ mb: 2 }} InputProps={{ readOnly: true }}/>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Status:{" "}
            <span style={{ 
              color: currentStatus === "new" ? "#d32f2f" : 
                     currentStatus === "rejected" ? "#d32f2f" :
                     currentStatus === "accepted" ? "#2e7d32" :
                     currentStatus === "submission_review" ? "#ed6c02" : 
                     "inherit" 
            }}>
              {currentStatus || ""}
            </span>
          </Typography>

          {/* Category Management Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Category:{" "}
              <span style={{ 
                fontWeight: "bold",
                color: currentCategory === 'active' ? '#4caf50' : 
                       currentCategory === 'archive' ? '#ff9800' : '#9e9e9e' 
              }}>
                {currentCategory || 'Uncategorized'}
              </span>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant={currentCategory === 'active' ? 'contained' : 'outlined'}
                color="success"
                onClick={() => updateApplicationCategory('active')}
              >
                Set Active
              </Button>
              <Button
                size="small"
                variant={currentCategory === 'archive' ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => updateApplicationCategory('archive')}
              >
                Archive
              </Button>
              <Button
                size="small"
                variant={!currentCategory ? 'contained' : 'outlined'}
                color="info"
                onClick={() => updateApplicationCategory(null)}
              >
                Clear Category
              </Button>
            </Box>
          </Box>

          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Non-Compliance Issues
            </Typography>
            {nonComplianceIssues.length > 0 ? (
              
              <Paper sx={{ p: 2, bgcolor: '#fff3f3' }}>
                <List sx={{ pl: 1 }}>
                  {nonComplianceIssues.map((issue, index) => (
                    <ListItem key={index} sx={{ px: 1, py: 0.5 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setSelectedIssue(issue)}
                        sx={{ 
                          justifyContent: "flex-start",
                          textAlign: "left",
                          textTransform: "none"
                        }}
                      >
                        {typeof issue === 'object' ? issue.control_reference || 'Issue Details' : issue.split('\n')[0]}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No non-compliance issues found.
              </Typography>
            )}

            {/* Dialog for issue details */}
            <Dialog 
              open={!!selectedIssue} 
              onClose={() => setSelectedIssue(null)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Non-Compliance Issue Details</DialogTitle>
              <DialogContent>
                {selectedIssue && (
                  <Box sx={{ pt: 2 }}>
                    {typeof selectedIssue === 'object' ? (
                      <>
                        <Typography variant="h6" gutterBottom>
                          {selectedIssue.control_reference || "Issue Details"}
                        </Typography>
                        <List>
                          {selectedIssue.control_type && (
                            <ListItem>
                              <Typography variant="body1">
                                <strong>Control Type:</strong> {selectedIssue.control_type}
                              </Typography>
                            </ListItem>
                          )}
                          {selectedIssue.issue_description && (
                            <ListItem>
                              <Typography variant="body1">
                                <strong>Issue:</strong> {selectedIssue.issue_description}
                              </Typography>
                            </ListItem>
                          )}
                          {selectedIssue.explanation && (
                            <ListItem>
                              <Typography variant="body1">
                                <strong>Explanation:</strong> {selectedIssue.explanation}
                              </Typography>
                            </ListItem>
                          )}
                          {selectedIssue.suggested_fix && (
                            <ListItem>
                              <Typography variant="body1">
                                <strong>Suggested Fix:</strong> {selectedIssue.suggested_fix}
                              </Typography>
                            </ListItem>
                          )}
                        </List>
                      </>
                    ) : (
                      <Typography variant="body1">
                        {selectedIssue}
                      </Typography>
                    )}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedIssue(null)}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setAssessmentText(applicationData.initial_assessment || "No assessment available.");
                setAssessmentOpen(true);
              }}
            >
              Show Initial Assessment
            </Button>
            <Button variant="outlined" color="primary" onClick={onAnalyze}>
              Analyze with POPPi
            </Button>
          </Box>
          <Dialog open={assessmentOpen} onClose={handleAssessmentClose} maxWidth="md" fullWidth>
            <DialogTitle>Initial Document Assessment</DialogTitle>
            <DialogContent>
                <AssessmentViewer 
                  appId={applicationId} 
                  assessmentId="initial"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setAssessmentOpen(false)}>Close</Button>
            </DialogActions>
            </Dialog>

          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Previous Assessments</Typography>
          <List>
            {assessments.length === 0 && (
              <ListItem>
                <Typography variant="body2" color="text.secondary">No previous assessments.</Typography>
              </ListItem>
            )}
            {assessments
              .filter(a => a.task !== "assessfiles")
              .map(a => (
                <ListItemButton 
                  key={a.id} 
                  onClick={() => { 
                    console.log('Assessment clicked:', { 
                      assessmentId: a.id, 
                      applicationId: applicationId, 
                      assessment: a 
                    });
                    setSelectedAssessment(a); 
                  }}
                >
                <Typography variant="body2">{a.task} assessment - {a.timestamp}</Typography>
              </ListItemButton>
            ))}
          </List>
          <Dialog open={!!selectedAssessment} onClose={() => setSelectedAssessment(null)} maxWidth="md" fullWidth>
            <DialogTitle>Assessment ({selectedAssessment && selectedAssessment.timestamp})</DialogTitle>
            <DialogContent>
              {selectedAssessment && (
                <AssessmentViewer 
                  appId={applicationId} 
                  assessmentId={selectedAssessment.id} 
                  threadId={selectedAssessment.thread_id}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDownloadWordChecklist} color="primary" variant="outlined">
                Download Word Checklist
              </Button>
              <Button onClick={handleDownloadPDF} color="primary" variant="outlined">
                Download PDF
              </Button>
              <Button onClick={() => setSelectedAssessment(null)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* PDF Viewer Dialog */}
          <Dialog 
            open={!!selectedFile} 
            onClose={() => setSelectedFile(null)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {selectedFile?.name}
              <Box>
                <Button 
                  disabled={pageNumber <= 1} 
                  onClick={handlePrevPage}
                  sx={{ mr: 1 }}
                >
                  Previous
                </Button>
                <Typography component="span">
                  Page {pageNumber} of {numPages}
                </Typography>
                <Button 
                  disabled={pageNumber >= numPages} 
                  onClick={handleNextPage}
                  sx={{ ml: 1 }}
                >
                  Next
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent>
              {selectedFile && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Document
                    file={selectedFile?.url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<Typography>Loading PDF...</Typography>}
                  >
                    <Page 
                      pageNumber={pageNumber} 
                      renderTextLayer={false}
                      scale={1.5}
                    />
                  </Document>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedFile(null)}>Close</Button>
              <Button 
                component="a" 
                href={selectedFile?.url} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Download
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>
    </Box>
  );
}